import React, { useEffect, useMemo, useState } from 'react';
import {
  CalendarDays,
  CalendarOff,
  CheckCircle,
  Clock,
  Copy,
  Edit3,
  Loader2,
  Plus,
  Search,
  Trash2,
  UserRound,
  X,
} from 'lucide-react';
import {
  createDefaultSchedule,
  removeEmployee,
  removeVacationRange,
  saveEmployee,
  saveVacationRange,
  subscribeToEmployees,
  subscribeToVacations,
  WEEK_DAYS,
} from '../../services/employeeAvailability';

const emptyEmployee = () => ({
  _createOnly: true,
  _duplicateSourceName: '',
  email: '',
  displayName: '',
  timezone: 'Europe/Madrid',
  employmentStartDate: '',
  employmentEndDate: '',
  weeklySchedule: createDefaultSchedule(),
});

const formatDate = (date) => date.split('-').reverse().join('/');

const SCHEDULE_DAYS = [
  { id: 'monday', letter: 'L' },
  { id: 'tuesday', letter: 'M' },
  { id: 'wednesday', letter: 'X' },
  { id: 'thursday', letter: 'J' },
  { id: 'friday', letter: 'V' },
  { id: 'saturday', letter: 'S' },
  { id: 'sunday', letter: 'D' },
];

const formatScheduleTime = (time) => {
  const [hour, minute = '00'] = String(time).split(':');
  return `${Number(hour)}:${minute}`;
};

const workingIntervals = (day) => {
  if (!day?.enabled) return [];
  return (day.intervals || []).filter((interval) => interval?.start && interval?.end);
};

const sameIntervals = (first, second) =>
  first.length === second.length && first.every((interval, index) =>
    interval.start === second[index].start && interval.end === second[index].end
  );

export const formatWeeklySchedule = (weeklySchedule) => {
  const groups = [];

  SCHEDULE_DAYS.forEach((day, index) => {
    const intervals = workingIntervals(weeklySchedule?.[day.id]);
    if (!intervals.length) return;

    const currentGroup = groups[groups.length - 1];
    const continuesGroup = currentGroup
      && currentGroup.endIndex === index - 1
      && sameIntervals(currentGroup.intervals, intervals);

    if (continuesGroup) {
      currentGroup.endLetter = day.letter;
      currentGroup.endIndex = index;
      return;
    }

    groups.push({
      startLetter: day.letter,
      endLetter: day.letter,
      endIndex: index,
      intervals,
    });
  });

  if (!groups.length) return ['Sin horario'];

  return groups.map((group) => {
    const days = group.startLetter === group.endLetter
      ? group.startLetter
      : `${group.startLetter}-${group.endLetter}`;
    const hours = group.intervals
      .map((interval) => `${formatScheduleTime(interval.start)}-${formatScheduleTime(interval.end)}`)
      .join(', ');
    return `${days} ${hours}`;
  });
};

const groupVacationRanges = (vacations) => {
  const sorted = [...vacations].sort((first, second) =>
    first.date.localeCompare(second.date)
  );

  return sorted.reduce((ranges, vacation) => {
    const previous = ranges[ranges.length - 1];
    if (previous) {
      const dayAfterPrevious = new Date(`${previous.endDate}T00:00:00Z`);
      dayAfterPrevious.setUTCDate(dayAfterPrevious.getUTCDate() + 1);
      if (dayAfterPrevious.toISOString().slice(0, 10) === vacation.date) {
        previous.endDate = vacation.date;
        return ranges;
      }
    }
    ranges.push({
      startDate: vacation.date,
      endDate: vacation.date,
    });
    return ranges;
  }, []);
};

const getLocalDayAndTime = (timezone) => {
  const now = new Date();
  const day = new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    timeZone: timezone,
  }).format(now).toLowerCase();
  const parts = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: timezone,
  }).formatToParts(now);
  const hour = parts.find((part) => part.type === 'hour')?.value || '00';
  const minute = parts.find((part) => part.type === 'minute')?.value || '00';
  const dateParts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: timezone,
  }).formatToParts(now);
  const year = dateParts.find((part) => part.type === 'year')?.value;
  const month = dateParts.find((part) => part.type === 'month')?.value;
  const dateOfMonth = dateParts.find((part) => part.type === 'day')?.value;
  return { day, time: `${hour}:${minute}`, date: `${year}-${month}-${dateOfMonth}` };
};

const getTodayDetails = (employee, vacations) => {
  try {
    const { day, time, date } = getLocalDayAndTime(
      employee.timezone || 'Europe/Madrid'
    );
    const vacation = vacations.find((exception) => exception.date === date);
    const notStarted = employee.employmentStartDate &&
      date < employee.employmentStartDate;
    const hasEnded = employee.employmentEndDate &&
      date > employee.employmentEndDate;
    const schedule = employee.weeklySchedule?.[day];
    const intervals = schedule?.enabled ? schedule.intervals || [] : [];
    const available = !notStarted && !hasEnded && !vacation && intervals.some(
      (interval) => interval.start <= time && time < interval.end
    );
    return {
      intervals,
      available,
      vacation,
      employmentStatus: notStarted ? 'not-started' : hasEnded ? 'ended' : 'active',
    };
  } catch (error) {
    return {
      intervals: [],
      available: false,
      vacation: null,
      employmentStatus: 'active',
    };
  }
};

const EmployeeForm = ({ employee, onCancel, onSave, saving }) => {
  const [form, setForm] = useState(employee);
  const [validationError, setValidationError] = useState('');

  const updateDay = (dayId, changes) => {
    setForm((current) => ({
      ...current,
      weeklySchedule: {
        ...current.weeklySchedule,
        [dayId]: {
          ...(current.weeklySchedule[dayId] || { enabled: false, intervals: [] }),
          ...changes,
        },
      },
    }));
  };

  const updateTime = (dayId, field, value) => {
    const currentDay = form.weeklySchedule[dayId];
    const interval = currentDay.intervals?.[0] || { start: '09:00', end: '18:00' };
    updateDay(dayId, {
      intervals: [{ ...interval, [field]: value }],
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const email = form.email.trim().toLowerCase();

    if (!/^[^@\s]+@sonneil\.com$/.test(email)) {
      setValidationError('Introduce un email corporativo @sonneil.com válido.');
      return;
    }

    if (form.employmentStartDate && form.employmentEndDate &&
        form.employmentStartDate > form.employmentEndDate) {
      setValidationError('La fecha final no puede ser anterior a la inicial.');
      return;
    }

    const invalidDay = WEEK_DAYS.find(({ id }) => {
      const day = form.weeklySchedule[id];
      const interval = day?.intervals?.[0];
      return day?.enabled && (!interval || interval.start >= interval.end);
    });

    if (invalidDay) {
      setValidationError(`Revisa el horario de ${invalidDay.label}.`);
      return;
    }

    setValidationError('');
    onSave({ ...form, email });
  };

  return (
    <form onSubmit={handleSubmit} className="card p-6 border border-border mb-8">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">
            {employee._duplicateSourceName
              ? `Duplicar horario de ${employee._duplicateSourceName}`
              : employee._createOnly
              ? 'Añadir empleado'
              : 'Editar empleado'}
          </h2>
          <p className="text-sm text-text-secondary mt-1">
            Define las fechas laborales y el horario semanal habitual.
          </p>
        </div>
        <button type="button" onClick={onCancel} className="btn-tertiary btn-sm" aria-label="Cerrar">
          <X size={18} />
        </button>
      </div>

      {validationError && (
        <div className="mb-5 p-3 rounded-lg bg-error/10 text-error text-sm" role="alert">
          {validationError}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <label className="block">
          <span className="block text-sm font-medium text-text-primary mb-2">Email corporativo</span>
          <input
            type="email"
            className="input"
            value={form.email}
            disabled={Boolean(employee.email)}
            onChange={(event) => setForm({ ...form, email: event.target.value })}
            placeholder="persona@sonneil.com"
            required
          />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-text-primary mb-2">Nombre</span>
          <input
            className="input"
            value={form.displayName}
            onChange={(event) => setForm({ ...form, displayName: event.target.value })}
            placeholder="Nombre y apellidos"
            required
          />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-text-primary mb-2">Fecha de inicio</span>
          <input
            type="date"
            className="input"
            value={form.employmentStartDate}
            onChange={(event) => setForm({ ...form, employmentStartDate: event.target.value })}
          />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-text-primary mb-2">Fecha final</span>
          <input
            type="date"
            className="input"
            value={form.employmentEndDate}
            onChange={(event) => setForm({ ...form, employmentEndDate: event.target.value })}
          />
        </label>
        <label className="block md:col-span-2">
          <span className="block text-sm font-medium text-text-primary mb-2">Zona horaria</span>
          <select
            className="input"
            value={form.timezone}
            onChange={(event) => setForm({ ...form, timezone: event.target.value })}
          >
            <option value="Europe/Madrid">España peninsular — Europe/Madrid</option>
            <option value="Atlantic/Canary">Islas Canarias — Atlantic/Canary</option>
            <option value="Europe/London">Reino Unido — Europe/London</option>
          </select>
        </label>
      </div>

      <h3 className="font-semibold text-text-primary mb-3">Horario semanal</h3>
      <div className="border border-border rounded-xl overflow-hidden mb-6">
        {WEEK_DAYS.map(({ id, label }) => {
          const day = form.weeklySchedule[id] || { enabled: false, intervals: [] };
          const interval = day.intervals?.[0] || { start: '09:00', end: '18:00' };
          return (
            <div
              key={id}
              className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-3 p-4 border-b last:border-b-0 border-border-light"
            >
              <label className="flex items-center gap-3 font-medium text-text-primary">
                <input
                  type="checkbox"
                  checked={day.enabled}
                  onChange={(event) => updateDay(id, {
                    enabled: event.target.checked,
                    intervals: event.target.checked ? [interval] : [],
                  })}
                  className="rounded border-border text-primary focus:ring-primary"
                />
                {label}
              </label>
              {day.enabled ? (
                <div className="flex items-center gap-2">
                  <input
                    type="time"
                    className="input py-2"
                    value={interval.start}
                    onChange={(event) => updateTime(id, 'start', event.target.value)}
                    aria-label={`Hora inicial de ${label}`}
                  />
                  <span className="text-text-tertiary">a</span>
                  <input
                    type="time"
                    className="input py-2"
                    value={interval.end}
                    onChange={(event) => updateTime(id, 'end', event.target.value)}
                    aria-label={`Hora final de ${label}`}
                  />
                </div>
              ) : (
                <span className="text-sm text-text-tertiary self-center">No trabaja</span>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="btn-secondary btn-sm">
          Cancelar
        </button>
        <button type="submit" disabled={saving} className="btn-primary btn-sm disabled:opacity-50">
          {saving && <Loader2 size={17} className="animate-spin" />}
          Guardar
        </button>
      </div>
    </form>
  );
};

const VacationForm = ({ employees, onCancel, onSave, saving }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    employeeEmail: employees[0]?.email || '',
    startDate: today,
    endDate: today,
  });
  const [validationError, setValidationError] = useState('');

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!form.employeeEmail) {
      setValidationError('Selecciona un empleado.');
      return;
    }
    if (form.startDate > form.endDate) {
      setValidationError('La fecha final no puede ser anterior a la inicial.');
      return;
    }
    const rangeLength = Math.floor(
      (new Date(`${form.endDate}T00:00:00Z`) -
        new Date(`${form.startDate}T00:00:00Z`)) / 86400000
    ) + 1;
    if (rangeLength > 366) {
      setValidationError('El periodo no puede superar 366 días.');
      return;
    }
    setValidationError('');
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit} className="card p-6 border border-border mb-8">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">Añadir vacaciones</h2>
          <p className="text-sm text-text-secondary mt-1">
            La persona aparecerá como no disponible durante todo el periodo.
          </p>
        </div>
        <button type="button" onClick={onCancel} className="btn-tertiary btn-sm" aria-label="Cerrar">
          <X size={18} />
        </button>
      </div>

      {validationError && (
        <div className="mb-5 p-3 rounded-lg bg-error/10 text-error text-sm" role="alert">
          {validationError}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <label className="block md:col-span-2">
          <span className="block text-sm font-medium text-text-primary mb-2">Empleado</span>
          <select
            className="input"
            value={form.employeeEmail}
            onChange={(event) => setForm({ ...form, employeeEmail: event.target.value })}
            required
          >
            {employees.map((employee) => (
              <option key={employee.email} value={employee.email}>
                {employee.displayName} — {employee.email}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-text-primary mb-2">Desde</span>
          <input
            type="date"
            className="input"
            value={form.startDate}
            onChange={(event) => setForm({ ...form, startDate: event.target.value })}
            required
          />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-text-primary mb-2">Hasta</span>
          <input
            type="date"
            className="input"
            value={form.endDate}
            onChange={(event) => setForm({ ...form, endDate: event.target.value })}
            required
          />
        </label>
      </div>

      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="btn-secondary btn-sm">
          Cancelar
        </button>
        <button type="submit" disabled={saving} className="btn-primary btn-sm disabled:opacity-50">
          {saving && <Loader2 size={17} className="animate-spin" />}
          Añadir vacaciones
        </button>
      </div>
    </form>
  );
};

const AvailabilityPage = ({ user, roles }) => {
  const [employees, setEmployees] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [addingVacation, setAddingVacation] = useState(false);
  const [saving, setSaving] = useState(false);
  const isEditor = roles.admin || roles.hr;

  useEffect(() => subscribeToEmployees(
    (nextEmployees) => {
      setEmployees(nextEmployees);
      setLoading(false);
    },
    (nextError) => {
      console.error('Error loading employees:', nextError);
      setError('No se pudo cargar la disponibilidad.');
      setLoading(false);
    }
  ), []);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    return subscribeToVacations(
      today,
      setVacations,
      (nextError) => {
        console.error('Error loading vacations:', nextError);
        setError('No se pudieron cargar las vacaciones.');
      }
    );
  }, []);

  const filteredEmployees = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return employees;
    return employees.filter((employee) =>
      `${employee.displayName} ${employee.email}`.toLowerCase().includes(term)
    );
  }, [employees, search]);

  const handleSave = async (employee) => {
    setSaving(true);
    setError('');
    try {
      await saveEmployee(employee, user.uid, {
        createOnly: employee._createOnly,
      });
      setEditing(null);
      setSuccess('Horario guardado correctamente.');
      window.setTimeout(() => setSuccess(''), 3000);
    } catch (saveError) {
      console.error('Error saving employee:', saveError);
      setError(
        saveError.code === 'employee-exists'
          ? 'Ya existe un empleado con este email. Abre su ficha para editarlo.'
          : 'No se pudo guardar el horario. Comprueba tus permisos.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (employee) => {
    if (!window.confirm(
      `¿Eliminar a ${employee.displayName} y todos sus periodos de vacaciones?`
    )) return;
    setError('');
    try {
      await removeEmployee(employee.email);
      setSuccess('Empleado eliminado de la lista.');
    } catch (deleteError) {
      console.error('Error deleting employee:', deleteError);
      setError('No se pudo eliminar el empleado.');
    }
  };

  const handleDuplicate = (employee) => {
    setAddingVacation(false);
    setEditing({
      ...emptyEmployee(),
      _duplicateSourceName: employee.displayName,
      displayName: '',
      email: '',
      timezone: employee.timezone,
      employmentStartDate: '',
      employmentEndDate: '',
      weeklySchedule: JSON.parse(JSON.stringify(
        employee.weeklySchedule || createDefaultSchedule()
      )),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSaveVacation = async (vacation) => {
    setSaving(true);
    setError('');
    try {
      await saveVacationRange({
        ...vacation,
        userId: user.uid,
      });
      setAddingVacation(false);
      setSuccess('Vacaciones añadidas correctamente.');
      window.setTimeout(() => setSuccess(''), 3000);
    } catch (saveError) {
      console.error('Error saving vacation:', saveError);
      setError('No se pudieron guardar las vacaciones. Comprueba tus permisos.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveVacation = async (employee, vacationRange) => {
    const rangeLabel = vacationRange.startDate === vacationRange.endDate
      ? formatDate(vacationRange.startDate)
      : `${formatDate(vacationRange.startDate)}–${formatDate(vacationRange.endDate)}`;
    if (!window.confirm(`¿Eliminar las vacaciones de ${rangeLabel}?`)) return;
    setError('');
    try {
      await removeVacationRange(
        employee.email,
        vacationRange.startDate,
        vacationRange.endDate
      );
      setSuccess('Periodo de vacaciones eliminado.');
    } catch (deleteError) {
      console.error('Error deleting vacation:', deleteError);
      setError('No se pudo eliminar el día de vacaciones.');
    }
  };

  return (
    <div className="animate-stagger">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-text-primary mb-2">Disponibilidad del equipo</h1>
          <p className="text-text-secondary">
            Consulta las fechas y los horarios habituales de todos los empleados.
          </p>
        </div>
        {isEditor && !editing && !addingVacation && (
          <div className="flex flex-wrap gap-3">
            {employees.length > 0 && (
              <button
                type="button"
                onClick={() => setAddingVacation(true)}
                className="btn-secondary btn-sm"
              >
                <CalendarOff size={18} />
                Añadir vacaciones
              </button>
            )}
            <button
              type="button"
              onClick={() => setEditing(emptyEmployee())}
              className="btn-primary btn-sm"
            >
              <Plus size={18} />
              Añadir empleado
            </button>
          </div>
        )}
      </div>

      {roles.hr && !roles.admin && (
        <div className="mb-6 p-4 bg-primary-lighter/30 rounded-xl text-sm text-text-secondary">
          Tienes permisos de RRHH para añadir, editar y eliminar empleados y sus horarios.
        </div>
      )}

      <div className="mb-6 p-4 bg-info/10 rounded-xl text-sm text-text-secondary">
        PayFit sigue siendo el sistema oficial para solicitar y aprobar vacaciones.
        Esta página muestra la disponibilidad del equipo una vez que RRHH la registra.
      </div>

      {error && <div className="mb-6 p-4 bg-error/10 text-error rounded-xl" role="alert">{error}</div>}
      {success && (
        <div className="mb-6 p-4 bg-success/10 text-success rounded-xl flex items-center gap-2" role="status">
          <CheckCircle size={18} /> {success}
        </div>
      )}

      {editing && (
        <EmployeeForm
          employee={editing}
          onCancel={() => setEditing(null)}
          onSave={handleSave}
          saving={saving}
        />
      )}

      {addingVacation && (
        <VacationForm
          employees={employees}
          onCancel={() => setAddingVacation(false)}
          onSave={handleSaveVacation}
          saving={saving}
        />
      )}

      <div className="relative max-w-lg mb-6">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-tertiary" />
        <input
          className="input pl-11"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Buscar por nombre o email"
          aria-label="Buscar empleados"
        />
      </div>

      {loading ? (
        <div className="py-16 flex justify-center text-primary">
          <Loader2 size={32} className="animate-spin" />
        </div>
      ) : filteredEmployees.length === 0 ? (
        <div className="card p-10 text-center">
          <UserRound size={40} className="text-text-tertiary mx-auto mb-3" />
          <h2 className="font-semibold text-text-primary">
            {employees.length === 0 ? 'Todavía no hay empleados' : 'No hay resultados'}
          </h2>
          <p className="text-sm text-text-secondary mt-1">
            {employees.length === 0 && isEditor
              ? 'Añade el primer empleado mediante su email corporativo.'
              : 'Prueba con otra búsqueda.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredEmployees.map((employee) => {
            const employeeVacations = vacations.filter(
              (vacation) => vacation.employeeEmail === employee.email
            );
            const vacationRanges = groupVacationRanges(employeeVacations);
            const today = getTodayDetails(employee, employeeVacations);
            const statusLabel = today.employmentStatus === 'not-started'
              ? 'Aún no ha comenzado'
              : today.employmentStatus === 'ended'
              ? 'Ya no trabaja'
              : today.vacation
              ? 'De vacaciones'
              : today.available
              ? 'Disponible ahora'
              : 'No disponible';
            const statusClass = today.employmentStatus !== 'active'
              ? 'badge'
              : today.vacation
              ? 'badge-warning'
              : today.available
              ? 'badge-success'
              : 'badge';
            return (
              <article key={employee.id} className="card p-5 border border-transparent hover:border-primary/20">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="font-semibold text-text-primary truncate">{employee.displayName}</h2>
                    <p className="text-sm text-text-tertiary truncate">{employee.email}</p>
                  </div>
                  <span className={statusClass}>{statusLabel}</span>
                </div>

                <div className="mt-5 space-y-3 text-sm">
                  <div className="flex items-start gap-2 text-text-secondary">
                    <Clock size={17} className="text-primary shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      {formatWeeklySchedule(employee.weeklySchedule).map((line) => (
                        <div key={line}>{line}</div>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-text-secondary">
                    <CalendarDays size={17} className="text-primary" />
                    {employee.employmentStartDate
                      ? formatDate(employee.employmentStartDate)
                      : 'Sin fecha inicial'}
                    {employee.employmentEndDate
                      ? ` — ${formatDate(employee.employmentEndDate)}`
                      : ''}
                  </div>
                </div>

                {employeeVacations.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-border-light">
                    <p className="text-xs font-semibold uppercase tracking-wide text-text-tertiary mb-2">
                      Próximas vacaciones
                    </p>
                    <div className="space-y-2">
                      {vacationRanges.slice(0, 3).map((vacationRange) => (
                        <div
                          key={vacationRange.startDate}
                          className="flex items-center justify-between gap-2 text-sm"
                        >
                          <span className="text-text-secondary">
                            {vacationRange.startDate === vacationRange.endDate
                              ? formatDate(vacationRange.startDate)
                              : `${formatDate(vacationRange.startDate)}–${formatDate(vacationRange.endDate)}`}
                          </span>
                          {isEditor && (
                            <button
                              type="button"
                              onClick={() => handleRemoveVacation(employee, vacationRange)}
                              className="text-text-tertiary hover:text-error transition-colors"
                              aria-label={`Eliminar vacaciones desde ${vacationRange.startDate}`}
                            >
                              <X size={15} />
                            </button>
                          )}
                        </div>
                      ))}
                      {vacationRanges.length > 3 && (
                        <p className="text-xs text-text-tertiary">
                          +{vacationRanges.length - 3} periodos adicionales
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {isEditor && (
                  <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-border-light">
                    <button
                      type="button"
                      onClick={() => handleDelete(employee)}
                      className="btn-tertiary btn-sm text-error"
                      aria-label={`Eliminar a ${employee.displayName}`}
                    >
                      <Trash2 size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDuplicate(employee)}
                      className="btn-tertiary btn-sm"
                      title="Duplicar horario"
                    >
                      <Copy size={16} />
                      Duplicar
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditing({
                        ...emptyEmployee(),
                        ...employee,
                        _createOnly: false,
                        _duplicateSourceName: '',
                        weeklySchedule: {
                          ...createDefaultSchedule(),
                          ...employee.weeklySchedule,
                        },
                      })}
                      className="btn-secondary btn-sm"
                    >
                      <Edit3 size={16} />
                      Editar
                    </button>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AvailabilityPage;
